import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Button } from "../../components/base/Button";
import { Toggle } from "../../components/base/Toggle";
import LoadingWheel from "../../components/shared/LoadingWheel";
import ConfirmModal from "../../components/shared/Confirm/ConfirmModal";
import { usePageNameContext } from "../../contexts/PageTitleContext";

import AiPromptModal from "../../components/InitialPriceEstimate/AiPromptModal";
import AiPromptSettings from "../../components/InitialPriceEstimate/AiPromptSettings";
import BasicSettings from "../../components/InitialPriceEstimate/BasicSettings";
import SerpSettingsModal from "../../components/InitialPriceEstimate/SerpSettingsModal";
import CategoryList from "../../components/InitialPriceEstimate/CategoryList";
import SearchFieldToggles from "../../components/InitialPriceEstimate/SearchFieldToggles";
import SearchTermPreview from "../../components/InitialPriceEstimate/SearchTermPreview";
import CategoryPickerModal from "../../components/TitleConfiguration/CategoryPickerModal";

import {
  useInitialPriceEstimate,
  useInitialPriceEstimateCategories,
} from "../../data/InitialPriceEstimate/useInitialPriceEstimate";
import useInitialPriceEstimateMutations from "../../data/InitialPriceEstimate/useInitialPriceEstimateMutations";
import {
  IInitialPriceEstimateCategorySummary,
  IInitialPriceEstimateRequest,
  IInitialPriceEstimateResponse,
} from "../../models/Category/InitialPriceEstimate";

/** Strips the response-only fields so what we send back matches InitialPriceEstimateRequest. */
function toRequest(
  response: IInitialPriceEstimateResponse,
): IInitialPriceEstimateRequest {
  return {
    isActive: response.isActive,
    searchKeyword: response.searchKeyword,
    ebayCategoryId: response.ebayCategoryId,
    searchFields: response.searchFields,
    sortOrder: response.sortOrder,
    aiPrompt: response.aiPrompt,
  };
}

/**
 * Initial Price Estimate: the part categories Core prices from eBay active listings when a lead or
 * quote arrives, and how each search term is built from the vehicle. Global across customers; it
 * reaches them through the category sync.
 */
export default function InitialPriceEstimatePage() {
  const { setInfo } = usePageNameContext();
  const navigate = useNavigate();
  const { categoryId } = useParams();

  const [searchTerm, setSearchTerm] = useState("");
  const [form, setForm] = useState<IInitialPriceEstimateRequest | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [serpOpen, setSerpOpen] = useState(false);
  const [aiPromptOpen, setAiPromptOpen] = useState(false);
  const [pendingRemoval, setPendingRemoval] =
    useState<IInitialPriceEstimateCategorySummary>();

  const { categories } = useInitialPriceEstimateCategories({
    searchTerm,
    configured: true,
  });
  const { config } = useInitialPriceEstimate(categoryId);
  const { create, remove, save } = useInitialPriceEstimateMutations();

  const configured = useMemo(() => categories.data ?? [], [categories.data]);

  useEffect(() => {
    setInfo({
      name: "Initial Price Estimate",
      desc: "Choose which part categories are priced from eBay when a lead or quote arrives, and how each search is built.",
      pageTitle: "Initial Price Estimate",
    });
  }, [setInfo]);

  // Land on the first priced category, and step away from one that has just been removed.
  useEffect(() => {
    // A failed load is an unknown list, not an empty one - do not navigate away on that basis.
    if (categories.isLoading || categories.isError || searchTerm) return;

    const stillExists =
      !!categoryId && configured.some((c) => c.partCategoryId === categoryId);

    if (stillExists) return;

    if (configured.length > 0) {
      navigate(`/initial-price-estimate/${configured[0].partCategoryId}`, {
        replace: true,
      });
    } else if (categoryId) {
      navigate("/initial-price-estimate", { replace: true });
    }
  }, [
    categoryId,
    configured,
    categories.isLoading,
    categories.isError,
    searchTerm,
    navigate,
  ]);

  // Reload the form whenever the server hands us a different category's config.
  useEffect(() => {
    if (!config.data) return;

    setForm(toRequest(config.data));
  }, [config.data]);

  const categoryName = config.data?.categoryName ?? "";
  const categoryEbayCategoryId = config.data?.categoryEbayCategoryId ?? null;
  const defaultAiPrompt = config.data?.defaultAiPrompt ?? "";

  const patchForm = (patch: Partial<IInitialPriceEstimateRequest>) =>
    setForm((current) => (current ? { ...current, ...patch } : current));

  const handleSave = () => {
    if (!form || !categoryId) return;

    save.mutate({ partCategoryId: categoryId, config: form });
  };

  const handleAdd = async (partCategoryIds: string[]) => {
    const result = await create.mutateAsync(partCategoryIds);

    setAddOpen(false);

    if (result.data?.length) {
      setSearchTerm("");
      navigate(`/initial-price-estimate/${result.data[0].partCategoryId}`);
    }
  };

  const handleRemove = async () => {
    if (!pendingRemoval) return;

    await remove.mutateAsync(pendingRemoval.partCategoryId);
    setPendingRemoval(undefined);
  };

  const showEditor = !!categoryId && !config.isLoading && !!form;

  return (
    <div className="p-4">
      <div className="mb-4 flex justify-end gap-2">
        <Button variant="outlined" onClick={() => setAiPromptOpen(true)}>
          AI prompt
        </Button>
        <Button variant="outlined" onClick={() => setSerpOpen(true)}>
          Serp settings
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
        <div className="xl:col-span-3">
          <CategoryList
            categories={configured}
            selectedId={categoryId}
            searchTerm={searchTerm}
            isLoading={categories.isLoading}
            error={(categories.error as Error) ?? null}
            onSearchChange={setSearchTerm}
            onSelect={(id) => navigate(`/initial-price-estimate/${id}`)}
            onAdd={() => setAddOpen(true)}
            onRemove={setPendingRemoval}
          />
        </div>

        {categories.isError ? (
          <div className="xl:col-span-9 rounded border border-red-200 bg-red-50 p-6">
            <p className="text-sm font-medium text-red-800">
              Could not load the priced categories
            </p>
            <p className="mt-1 text-sm text-red-700">
              {(categories.error as Error)?.message}
            </p>
            <p className="mt-3 text-xs text-red-700">
              A 404 usually means the API has not been restarted since this
              feature was added. A 500 usually means the
              InitialPriceEstimateConfigs table is missing — run the
              AddedInitialPriceEstimateConfig migration against the admin
              database.
            </p>
          </div>
        ) : !categoryId && !categories.isLoading ? (
          <div className="xl:col-span-9 flex flex-col items-center justify-center rounded border border-dashed border-gray-300 bg-white py-24 text-center">
            <p className="text-sm text-gray-600">No category selected.</p>
            <p className="mt-1 text-xs text-gray-500">
              Add the part categories you want priced on every new lead and
              quote.
            </p>
            <Button className="mt-4" onClick={() => setAddOpen(true)}>
              Add a category
            </Button>
          </div>
        ) : !showEditor ? (
          <div className="xl:col-span-9 flex items-center justify-center py-24">
            <LoadingWheel />
          </div>
        ) : (
          <div className="xl:col-span-9 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3 rounded border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-lg font-semibold">{categoryName}</h2>
                {!config.data?.isSaved && (
                  <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                    Not added yet — showing defaults
                  </span>
                )}
                <Toggle
                  labelRight
                  label="Active"
                  checked={form.isActive}
                  onChange={(value) => patchForm({ isActive: value })}
                />
              </div>

              <Button
                onClick={handleSave}
                disabled={save.isPending || form.searchFields.length === 0}
              >
                {save.isPending ? "Saving…" : "Save Changes"}
              </Button>
            </div>

            {!form.isActive && (
              <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                This category is inactive. It stays on the list but is not
                priced on new leads until it is turned back on.
              </p>
            )}

            <div className="grid grid-cols-1 gap-4 2xl:grid-cols-3">
              <div className="space-y-4 2xl:col-span-2">
                <SearchFieldToggles
                  selected={form.searchFields}
                  onChange={(searchFields) => patchForm({ searchFields })}
                />
                <BasicSettings
                  config={form}
                  categoryEbayCategoryId={categoryEbayCategoryId}
                  onChange={patchForm}
                />
                <AiPromptSettings
                  config={form}
                  defaultAiPrompt={defaultAiPrompt}
                  onChange={patchForm}
                />
              </div>

              <div className="space-y-4">
                <SearchTermPreview
                  config={form}
                  categoryName={categoryName}
                  categoryEbayCategoryId={categoryEbayCategoryId}
                  exampleSerpQuery={config.data?.exampleSerpQuery}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <SerpSettingsModal open={serpOpen} setOpen={setSerpOpen} />

      <AiPromptModal open={aiPromptOpen} setOpen={setAiPromptOpen} />

      <CategoryPickerModal
        open={addOpen}
        setOpen={setAddOpen}
        useCategories={useInitialPriceEstimateCategories}
        configured={false}
        title="Add categories to price"
        description="Each category you pick is added with Year, Make and Model as its search fields, no keyword and its own eBay category, ready to edit. It is priced on new leads after the next category sync."
        emptyText="Every category is already priced."
        isSaving={create.isPending}
        confirmLabel={(count) =>
          `Add ${count} categor${count === 1 ? "y" : "ies"}`
        }
        onConfirm={handleAdd}
      />

      <ConfirmModal
        open={!!pendingRemoval}
        setOpen={() => setPendingRemoval(undefined)}
        title={`Remove ${pendingRemoval?.name ?? ""}?`}
        message="New leads and quotes stop being priced for this category after the next category sync. Estimates already on leads are kept. If you add it again later its settings start from the defaults."
        confirmButtonText="Remove"
        isLoading={remove.isPending}
        onConfirm={handleRemove}
      />
    </div>
  );
}
